/* ---------------------------------------------------------------------------
   Which department's register this session is working against.

   ONE build serves every tenant, so the profile is state rather than a compile
   constant. It is resolved once on boot, in this order, and the first answer
   wins:

     1. `?tenant=` in the URL. Development and support: somebody reproducing a
        DPWH screen on a machine signed in as DoR should not have to redeploy.
        Ignored where it names a tenant nobody has registered.
     2. `VITE_TENANT` at build time. This is what a single-department deployment
        sets, and it is why such a deployment needs no database round-trip and
        no picker: the build knows.
     3. The signed-in user's own `tenant` from `/api/health`. This is the real
        answer in a shared deployment, and it is the SERVER'S answer — the
        client never gets to choose which register it reads.
     4. `DEFAULT_TENANT` — Nepal DoR, the deployment this application was
        written for and the only one with data in it today.

   WHAT THIS IS NOT. It is not authorisation. Nothing here decides what anybody
   may read: every API route scopes its own queries by the tenant on the verified
   session (see functions/lib/tenant.ts), so a client that lies about which
   profile it is drawing gets DPWH's labels over Nepal's records and no rows it
   was not already entitled to. The profile decides what the interface SAYS. The
   server decides what it SHOWS.

   WHY A SWAP RE-RENDERS EVERYTHING. `setActive` bumps a counter the shell reads,
   and src/main.ts repaints from scratch on every store update — the same
   arrangement `setLocale` uses, for the same reason. There is no partially
   re-branded tree to reason about, and the alternative was threading a profile
   argument through several hundred call sites.
   --------------------------------------------------------------------------- */

import type { TenantId, TenantProfile } from './types.ts';
import { NEPAL_DOR } from './profiles/nepal-dor.ts';
import { PH_DPWH } from './profiles/ph-dpwh.ts';
import { PK_FWO } from './profiles/pk-fwo.ts';
import { BD_RHD } from './profiles/bd-rhd.ts';

/**
 * Every department this build can serve.
 *
 * Adding one is this line, one profile file, one row in `tenants` — and a seed
 * only where there is a register to bundle. `pk-fwo` has none: see the header of
 * src/tenant/profiles/pk-fwo.ts, and `seedFor` in ./seeds.ts, which returns an
 * empty register rather than somebody else's.
 *
 * It is deliberately a compiled array and not a database table: a profile
 * carries regular expressions and functions (`roadClasses`, `groupings`) that
 * cannot be stored as data without inventing an expression language, and a
 * tenant whose road classification lives in a row is a tenant whose
 * classification nobody can review in a diff.
 */
export const TENANTS: readonly TenantProfile[] = [NEPAL_DOR, PH_DPWH, PK_FWO, BD_RHD];

/**
 * The tenant a deployment with nothing configured is.
 *
 * Nepal DoR, because this application was written for it, its register is the
 * one with real data in it, and every row written before tenancy existed is
 * one of its rows. A new default would silently re-attribute them.
 */
export const DEFAULT_TENANT: TenantId = NEPAL_DOR.id;

const BY_ID = new Map(TENANTS.map((t) => [t.id, t]));

/** The profile with that id, or null. Never throws on an unknown string. */
export function byId(id: string | null | undefined): TenantProfile | null {
  return id ? BY_ID.get(id) ?? null : null;
}

export function isTenantId(value: unknown): value is TenantId {
  return typeof value === 'string' && BY_ID.has(value);
}

let current: TenantProfile = NEPAL_DOR;
let revision = 0;

/** The department in force. Every screen reads its branding and labels here. */
export function active(): TenantProfile {
  return current;
}

/**
 * Adopt a profile, and report whether that changed anything.
 *
 * Returns false for an unknown id and for the one already in force, so a caller
 * can decide whether a repaint is owed without comparing profiles itself. An
 * unknown id is ignored rather than throwing: it arrives from a URL and from a
 * server response, and neither is a programming error worth a blank screen.
 */
export function setActive(id: string | null | undefined): boolean {
  const next = byId(id);
  if (!next || next.id === current.id) return false;
  current = next;
  revision += 1;
  return true;
}

/**
 * How many times the tenant has changed this session.
 *
 * Read by anything holding a value derived from the profile — a fitted map view,
 * a cached rollup — so it can tell "the same numbers as last frame" from "a
 * different register entirely". Cheaper and harder to get wrong than comparing
 * profile identity at every such site.
 */
export function tenantRevision(): number {
  return revision;
}

/** Drop back to the default. Used by the tests, which must not leak state. */
export function resetTenant(): void {
  current = NEPAL_DOR;
  revision += 1;
}

/**
 * Resolve the tenant from everything known at boot, in the documented order.
 *
 * A pure function of its inputs so the precedence can actually be tested — the
 * order is the whole of this module's behaviour, and read from the three places
 * it comes from it was checked by nothing at all. The caller applies the result
 * with `setActive`.
 */
export function resolveTenant(sources: {
  /** `?tenant=` off the current location. */
  url?: string | null;
  /** `VITE_TENANT` as the build baked it in. */
  build?: string | null;
  /** What the server says this session's user belongs to. */
  session?: string | null;
}): TenantId {
  return (
    (isTenantId(sources.url) ? sources.url : null) ??
    (isTenantId(sources.build) ? sources.build : null) ??
    (isTenantId(sources.session) ? sources.session : null) ??
    DEFAULT_TENANT
  );
}

/**
 * The register this window was opened for, where it was opened for one: `?tenant=`
 * or the build's VITE_TENANT. Null for a build that names none, which takes the
 * signed-in session's register instead.
 *
 * WHAT A SIGN-IN ASKS FOR, AND WHAT A SESSION IS HELD TO. The server serves the
 * register the session signed in to (migration 0050), so a window opened for the
 * Philippines that finds itself holding a Nepal session must not draw it — that
 * is Nepal's bridges under the DPWH letterhead. See `refreshIdentity` in
 * src/core/store.ts.
 */
export function requestedTenant(search: string = typeof window === 'undefined' ? '' : window.location.search): TenantId | null {
  const url = new URLSearchParams(search).get('tenant');
  if (isTenantId(url)) return url;
  const build = buildTenant();
  return isTenantId(build) ? build : null;
}

/**
 * `VITE_TENANT`, where the build set one.
 *
 * Read through `import.meta.env` rather than `process.env` because this runs in
 * a browser; wrapped in a function so the tests, which import this module under
 * Node with no Vite around it, do not fault on the property access.
 */
export function buildTenant(): string | null {
  try {
    return (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_TENANT ?? null;
  } catch {
    return null;
  }
}
